/**
 * Fetches predictive search results and renders them under the header input.
 *
 * Uses the Section Rendering API, so the markup comes from
 * `sections/predictive-search.liquid` rather than being built here.
 */
const PREDICTIVE_SEARCH_DEBOUNCE = 300;
const PREDICTIVE_SEARCH_MIN_LENGTH = 1;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.abortController = null;
    this.debounceTimer = null;

    this.input?.addEventListener('input', () => this.onInput());
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    this.abortController?.abort();
  }

  onInput() {
    clearTimeout(this.debounceTimer);

    const terms = this.input.value.trim();

    if (terms.length < PREDICTIVE_SEARCH_MIN_LENGTH) {
      this.clear();
      return;
    }

    this.debounceTimer = setTimeout(() => this.search(terms), PREDICTIVE_SEARCH_DEBOUNCE);
  }

  async search(terms) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product',
      'resources[limit]': this.dataset.limit || '4',
      section_id: 'predictive-search',
    });

    this.setAttribute('loading', '');

    try {
      const response = await fetch(`${window.Shopify.routes.root}search/suggest?${params}`, {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(response.statusText);

      const markup = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('.predictive-search__results');

      this.results.replaceChildren(...(markup ? markup.childNodes : []));
      this.toggleAttribute('open', Boolean(markup));
    } catch (error) {
      if (error.name !== 'AbortError') this.clear();
    } finally {
      this.removeAttribute('loading');
    }
  }

  clear() {
    this.abortController?.abort();
    this.results?.replaceChildren();
    this.removeAttribute('open');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
